import { type, objMatch } from '../utils/assist'
import AsyncValidator from 'async-validator'
import { notEmpty } from '../utils/validate'

var uid = 1

// 公式定义
function D(ctx) {
    // 基础属性
    this.id = uid++
    this.predicate = ctx.predicate
    this.formula = ctx.formula
    this.row = ctx.row
    this.key = ctx.key
    this.index = ctx.index
    this.data = ctx.data
    this.columnKeys = ctx.columnKeys
    this.options = Object.assign({
        immediate: false // 是否立即执行
    }, ctx.options)

    // 兼容tree-data
    this.isTree = false
    if (this.row._isTreeNode) {
        this.node = this.row
        this.row = this.node.node
        this.isTree = true
    }

    // 附加属性
    this.keyIndex = this.columnKeys.indexOf(this.key)
    this.rows = this.isTree ? this.data.map(v => v.node) : this.data
    this.vm = null
    this.watcher = null
}

Object.assign(D.prototype, {
    // 查找行
    // all 返回所有满足条件的行数组
    rowWhere(option, all) {
        if (type(option) == "undefined") {
            return all ? [this.row] : this.row
        }

        if (type(option) == "number") {
            if (option < 0) {
                return null
            }

            return all ? [this.rows[option]] : this.rows[option]
        }

        if (type(option) === "object") {
            return all ? this.rows.filter(v => objMatch(v, option)) : this.rows.find(v => objMatch(v, option))
        }

        if (type(option) == "function") {
            return all ? this.rows.filter(option) : this.rows.find(option)
        }
    },

    // 相对行
    rowOffset(offset, predicate) {
        if (type(offset) == "number") {
            if (type(predicate) == "object") {
                return this.rows
                    .filter((r, i) => offset > 0 ? i > this.index : i < this.index)
                    .find(v => objMatch(v, predicate))
            }

            if (type(predicate) == "function") {
                return this.rows
                    .filter((r, i) => offset > 0 ? i > this.index : i < this.index)
                    .find(predicate)
            }

            return this.rows[this.index + offset]
        }
    },

    // 相对key
    keyOffset(offset) {
        if (type(offset) == "number") {
            return this.columnKeys[this.keyIndex + offset]
        }
    },

    // 解除监听
    unwatch() {
        if (this.watcher) {
            this.watcher()
        }
    }
})

// 计算公式类
function Equation(ctx) {
    D.call(this, ctx)
}

Equation.prototype = Object.create(D.prototype)
Object.assign(Equation.prototype, {
    constructor: Equation,

    on(vm) {
        var formula = this.formula,
            d = this

        var { immediate } = d.options
        d.vm = vm
        d.watcher = vm.$watch(function() {
            return formula(d)
        }, function(newVal, oldVal) {
            d.row[d.key] = newVal
        }, {
            immediate
        })
    },

    off() {
        this.unwatch()
        this.vm = null
    }
})

// 校验公式类
function Verification(ctx) {
    D.call(this, ctx)

    this.defs = [{
        predicate: this.predicate,
        formula: this.formula
    }]
    this.valid = null
    this.errors = []
    this.rules = []
}

Verification.prototype = Object.create(D.prototype)
Object.assign(Verification.prototype, {
    constructor: Verification,

    on(vm) {
        var d = this,
            count = 1

        var { immediate } = d.options

        // 获取rules
        d.rules = d.defs.reduce((memo, v) => {
            memo.push(...v.formula(d))

            return memo
        }, [])

        // 替换required为notEmpty
        d.rules.forEach(v => {
            if (v.required) {
                v.validator = notEmpty
                delete v.required
            }
        })

        // depend
        d.vm = vm
        d.watcher = vm.$watch(function() {
            // self
            var value = d.row[d.key]

            // others
            d.rules.forEach(v => {
                if (v.validator) {
                    // fake (rule, value, callback, source, options)
                    v.validator(v, value, () => {}, d.row, { messages: AsyncValidator.messages })
                }
            })

            return ++count
        }, function(newVal, oldVal) {
            d.validate()
        }, {
            immediate
        })
    },

    off() {
        this.unwatch()
        this.vm = null

        this.valid = null
        this.rules = []
        this.errors = []
    },

    validate() {
        var rules = this.rules,
            index = this.index,
            d = this

        if (rules && rules.length > 0) {
            let descriptor = {
                [d.key]: rules
            }

            const spec = new AsyncValidator(descriptor);

            d.valid = null
            return new Promise(function(resolve, reject) {
                spec.validate(d.row, { firstFields: true }, errors => {
                    // 设置错误提示消息
                    d.errors = errors || []

                    if (d.errors.length <= 0) {
                        d.valid = true
                        resolve()
                    } else {
                        d.valid = false
                        reject(d.errors)
                    }
                })
            })
        } else {
            d.valid = true
            return Promise.resolve()
        }
    },
})

export default {
    props: {
        // 公式定义函数
        formula: {
            type: Function,
        }
    },

    data() {
        return {
            // 计算公式
            equations: [],
            // 验证公式
            validations: [],
            validationMap: {},
        }
    },

    computed: {
        // 获取行数据集合
        flatData() {
            return this.data
        },
        // 获取列名集合
        columnKeys() {
            return this.cloneColumns.map(v => v.key)
        },
    },

    methods: {
        // 定义计算公式
        equation(predicate, formula, options) {
            var data = this.flatData,
                columnKeys = this.columnKeys,
                vm = this

            // 唯一性判断
            function unique(d) {
                var source = vm.equations.find(v => v.index == d.index && v.key == d.key)

                if (source) {
                    throw new Error(`单元格[${d.index}, ${d.key}]最多只能定义1个计算公式。`)
                }
            }

            data.forEach((row, index) => {
                var ctx = {
                    data,
                    columnKeys,
                    row,
                    index,
                    predicate,
                    formula,
                    options
                }

                if (type(predicate) == "array") {
                    var [i, j] = predicate,
                    d = new Equation(Object.assign({ key: columnKeys[j] }, ctx))

                    // 行列存在
                    if (i == index && d.key) {
                        unique(d)

                        this.equations.push(d)
                    }
                }

                if (type(predicate) == "function") {
                    columnKeys.forEach(key => {
                        var d = new Equation(Object.assign({ key }, ctx))

                        if (predicate(d)) {
                            unique(d)

                            this.equations.push(d)
                        }
                    })
                }
            })
        },

        // 定义验证公式
        verification(predicate, formula, options) {
            var data = this.flatData,
                columnKeys = this.columnKeys,
                vm = this

            function add(d) {
                var source = vm.validations.find(v => v.index == d.index && v.key == d.key)

                // 校验规则合并
                if (source) {
                    source.defs.push({
                        predicate: d.predicate,
                        formula: d.formula
                    })

                    // some immediate = true
                    source.options.immediate = d.options.immediate || source.options.immediate
                }
                // 添加规则
                else {
                    vm.validations.push(d)
                    // 加入map
                    if (vm.validationMap[d.index]) {
                        vm.validationMap[d.index][d.key] = d
                    } else {
                        vm.validationMap[d.index] = {
                            [d.key]: d
                        }
                    }
                }
            }

            data.forEach((row, index) => {
                var ctx = {
                    data,
                    columnKeys,
                    row,
                    index,
                    predicate,
                    formula,
                    options
                }

                if (type(predicate) == "array") {
                    var [i, j] = predicate

                    // 行列存在
                    if (i == index && columnKeys[j]) {
                        var d = new Verification(Object.assign({ key: columnKeys[j] }, ctx))

                        add(d)
                    }
                }

                if (type(predicate) == "function") {
                    columnKeys.forEach(key => {
                        var d = new Verification(Object.assign({ key }, ctx))

                        if (predicate(d)) {
                            add(d)
                        }
                    })
                }
            })
        },

        // 解析公式
        formulaParse() {
            if (this.formula) {
                // 清空旧公式
                this.equations.forEach(v => v.off())
                this.validations.forEach(v => v.off())
                this.equations = []
                this.validations = []

                // 定义公式
                this.formula(this)

                // 启用公式
                this.equations.forEach(v => v.on(this))
                this.validations.forEach(v => v.on(this))
            }
        },

        // 整体校验
        validate(cb) {
            return new Promise((resolve, reject) => {
                var valid = true,
                    count = 0,
                    total = this.validations.length

                function done() {
                    count++

                    if (count === total) {
                        // 支持回调
                        if (type(cb) == "function") {
                            cb(valid)
                        }
                        // 支持Promise
                        valid ? resolve() : reject()
                    }
                }

                // 无需验证
                if (total == 0) {
                    return resolve()
                }

                this.validations.forEach(d => {
                    if (d.valid == false) {
                        valid = false
                        done()
                    } else if (d.valid == null) {
                        d.validate()
                            .catch(errors => valid = false)
                            .then(res => done())
                    } else {
                        done()
                    }
                })
            })
        },
    }
}